import React, { useEffect, useMemo, useState } from 'react'
import { AddToCartItems, AdonType, CustomKitLayoutProps } from '../../lib/type'
import { MultistepProgress } from '../MultistepProgress/MultistepProgress'
import { getSubscriptionStartDate } from 'stores/ooka_usa_store/airglobal/pylot-custom-kit-flow/src/utils/getSubscriptionStartDate'
import { useAddToCart } from '@components/alfakher/pylot-cart-manager'
import { useUI } from '@corratech/pylot-ui/context'
export const MD_INFINITE = 'md_end_infinite'
import s from '../DeviceSelection/DeviceTile.module.scss'
import { LoadingIndicator } from '@components/LoadingIndicator'
import {
    useCart,
    useRemoveItemFromCart,
    useUpdateCartItems
} from '@corratech/pylot-cart-manager'
import { useAlfakherContext } from '@components/alfakherContext/AlfakherContext'
import { CartItemUpdateInput } from '@pylot-data/pylotschema'
import { useRouter } from 'next/router'
import { useUser } from '@corratech/pylot-auth-manager'
import {
    trackAddedToCartFromItems,
    buildAddToCartItemFromProductVariant
} from '@components/Klaviyo/src/KlaviyoAddToCartTracker'
import { getStoreConfig } from 'config'

function buildAddonItems({
    product,
    addOns,
    baseUrl
}: {
    product: any
    addOns: { [sku: string]: { qty: number } }
    baseUrl: string
}) {
    const upsells = product?.upsell_products || []
    const items = []

    for (const [sku, { qty }] of Object.entries(addOns || {})) {
        const p = upsells.find((u: any) => u?.sku === sku) || null
        if (!p) {
            items.push({
                sku,
                title: sku,
                productId: sku,
                simpleProductId: sku,
                categories: [],
                price: 0,
                quantity: qty,
                url: baseUrl, // fallback
                imageUrl: undefined
            })
            continue
        }
        items.push(
            buildAddToCartItemFromProductVariant({
                product: p,
                variant: null,
                quantity: qty,
                url: `${baseUrl}p/${p?.url_key ?? ''}`,
                unitPriceOverride:
                    p?.price_range?.minimum_price?.final_price?.value ??
                    undefined,
                imageUrlOverride:
                    p?.thumbnail?.url || p?.small_image?.url || undefined
            })
        )
    }
    return items
}

const getUniqueItems = (arr: string[]) => [...new Set(arr)]

const CustomKitLayoutComponent = ({
    product,
    bundlePrice,
    setSelectedBundlePrice,
    setOrderMode,
    orderMode,
    setBillingPeriod,
    setIsMultiBuy,
    isMultiBuy,
    isLoading
}: CustomKitLayoutProps): JSX.Element => {
    const [cartActionInProgress, setCartActionInProgress] = useState(false)
    const { customer } = useUser()
    const { push, locale } = useRouter()
    const config = getStoreConfig(locale)
    const { addToCart, isAdding } = useAddToCart()
    const { cartEditId, setcartEditId } = useUI()
    const { updateCartItems } = useUpdateCartItems()
    const { data } = useCart()
    // @ts-expect-error
    const { setBundleStep } = useAlfakherContext()
    const {
        removeItemFromCart,
        isLoading: isRemoving
    } = useRemoveItemFromCart()

    const [selectedOptions, setSelectedOptions] = useState<string[]>([])

    /**
     * @TODO selectedSku planning to map it with
     * the bundle item for Add To Cart so keeping it as of now
     */
    const [addOns, setAddOns] = useState<{ [key: string]: AdonType }>({})

    const getSelectedUids = (): string[] => {
        const isStarterKit =
            product?.is_custom_kit && product?.sku === 'Starter-kit'
        if (isStarterKit) {
            const flavours = product?.items?.filter(
                (item) => item?.title === 'Flavours'
            )
            return (
                flavours?.reduce((acc: string[], curr) => {
                    curr?.options?.forEach((option) => {
                        acc.push(option?.uid as string)
                    })
                    return acc
                }, []) || []
            )
        }
        return []
    }

    const getItemsForDelete = useMemo(() => {
        // we need all items that are part if cart and product_upsell array,
        // but are not present in addOns
        const upsellSkus = product?.upsell_products?.map(
            (product) => product?.sku
        )

        const filteredItems = data?.data?.cart?.items?.filter((cartItem) => {
            const itemSku = cartItem?.product.sku || ''
            return (
                upsellSkus?.includes(itemSku) &&
                !Object.keys(addOns).includes(itemSku)
            )
        })

        return filteredItems
    }, [product?.upsell_products, data?.data?.cart?.items, addOns])

    const addProductHandler = async (
        multiBuySkus: string[] | null | undefined = null
    ) => {
        setCartActionInProgress(true)
        let isError = false
        const selectedUids = selectedOptions.filter((option) => option)
        const cartItems = data?.data?.cart?.items
        const quantity = cartEditId
            ? cartItems?.find((item) => item?.product.id === product?.id)
                  ?.quantity || 1
            : 1

        const addToCartItems: AddToCartItems = [
            {
                sku: product?.sku!,
                selected_options: getUniqueItems([
                    ...getSelectedUids(),
                    ...selectedUids
                ]),
                quantity
            }
        ]

        if (cartEditId) {
            await removeItemFromCart(cartEditId as string)
        }

        const itemsToUpdate: CartItemUpdateInput[] = []
        if (getItemsForDelete?.length) {
            getItemsForDelete.forEach((item) => {
                itemsToUpdate.push({
                    cart_item_uid: item?.uid! as string,
                    quantity: 0
                })
            })
        }

        if (Object.keys(addOns ?? {})?.length) {
            Object.keys(addOns).forEach(async (_sku) => {
                if (_sku !== product?.sku) {
                    const existingAddOn = cartItems?.find(
                        (item) => item?.product?.sku === _sku
                    )
                    if (existingAddOn) {
                        // Add-on exists in the cart, update it
                        itemsToUpdate.push({
                            cart_item_uid: existingAddOn?.uid! as string,
                            quantity: addOns[_sku]?.qty
                        })
                    } else {
                        // Add-on does not exist, add it
                        addToCartItems.push({
                            sku: addOns?.[_sku]?.sku,
                            quantity: addOns?.[_sku]?.qty
                        })
                    }
                }
            })
            // @ts-expect-error
            if (itemsToUpdate.length) await updateCartItems(itemsToUpdate)
        } else {
            const cartItems = data?.data?.cart?.items
            const addonsForDelete = cartItems?.filter(
                (item) =>
                    // @ts-expect-error
                    item?.product?.__typename === 'SimpleProduct' &&
                    product?.upsell_products?.some(
                        (product) => product?.id === item?.product?.id
                    )
            )
            if (addonsForDelete) {
                addonsForDelete.forEach(
                    async (_addon) =>
                        await removeItemFromCart(_addon?.uid as string)
                )
            }
        }

        if (addToCartItems.length > 0) {
            if (setIsMultiBuy) {
                setIsMultiBuy(true)
            }
            const response = await addToCart(
                addToCartItems,
                [product],
                [],
                // @ts-ignore
                undefined,
                // @ts-ignore
                true
            )
            if (multiBuySkus && setIsMultiBuy) {
                setIsMultiBuy(false)
            }
            isError =
                isError ||
                Boolean(
                    response?.errors?.length ?? response?.user_errors?.length
                )
        }
        if (!isError) {
            if (customer?.email) {
                const baseUrl = config.base.url.baseUrl

                const mainQty = cartEditId
                    ? data?.data?.cart?.items?.find(
                          (i) => i?.product?.id === product?.id
                      )?.quantity || 1
                    : 1

                const mainItem = buildAddToCartItemFromProductVariant({
                    product: product as any,
                    variant: null,
                    quantity: mainQty,
                    url: `${baseUrl}p/${product?.url_key}`,
                    unitPriceOverride:
                        bundlePrice?.discountPrice ??
                        bundlePrice?.price ??
                        undefined,
                    imageUrlOverride:
                        product?.thumbnail?.url ||
                        product?.small_image?.url ||
                        undefined
                })

                const addonItems = buildAddonItems({ product, addOns, baseUrl })

                trackAddedToCartFromItems([mainItem, ...addonItems], {
                    email: customer.email
                })

                setTimeout(() => {
                    push('/checkout')
                }, 150)
                return
            }

            await push('/checkout')
            return
        }
    }
    useEffect(() => {
        if (cartActionInProgress) return
        const cartItems = data?.data?.cart?.items
        const targetProduct = cartItems?.find(
            (item) => item?.uid === cartEditId
        )
        const _items: string[] = []
        targetProduct?.bundle_options?.map((option) => {
            option?.values.map((value) =>
                !_items.some((item) => value?.uid === item)
                    ? _items.push(value?.uid as string)
                    : value
            )
        })
        const upsellProducts = product?.upsell_products
        let _addons = {}
        cartItems?.map((item) => {
            const isUpsell = upsellProducts?.some(
                (prod) => prod?.id === item?.product?.id
            )

            if (isUpsell) {
                _addons = {
                    ..._addons,
                    [item?.product?.sku as string]: {
                        sku: item?.product?.sku,
                        qty: item?.quantity
                    }
                }
            }
        })
        setSelectedOptions((state) => getUniqueItems([...state, ..._items]))
        setAddOns(_addons)
    }, [
        product?.upsell_products,
        data?.data?.cart?.items,
        cartActionInProgress,
        setSelectedOptions,
        setAddOns,
        cartEditId
    ])

    useEffect(() => {
        return () => {
            setBundleStep(0)
            setcartEditId(undefined)
        }
    }, [])

    if (isLoading) {
        return (
            <div className={s['custom-kit-loader-container']}>
                <LoadingIndicator />
            </div>
        )
    }

    return (
        <div className={s['custom-kit-container']}>
            <MultistepProgress
                product={product}
                selectedOptions={selectedOptions}
                setSelectedOptions={setSelectedOptions}
                bundlePrice={bundlePrice}
                setSelectedBundlePrice={setSelectedBundlePrice}
                setOrderMode={setOrderMode}
                addOns={addOns}
                setAddOns={setAddOns}
                addProductHandler={addProductHandler}
                setBillingPeriod={setBillingPeriod}
                isAddToCartInProgress={
                    isAdding || isRemoving || isMultiBuy || cartActionInProgress
                }
            />
        </div>
    )
}

export const CustomKitLayout = React.memo(
    (props: CustomKitLayoutProps) => <CustomKitLayoutComponent {...props} />,
    (oldProps, newProps) =>
        JSON.stringify(oldProps) === JSON.stringify(newProps)
)

CustomKitLayout.displayName = 'MemoCustomKitLayout'
